import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getUsers } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { ScrollArea } from '../components/ui/scroll-area';
import { useNavigate } from 'react-router-dom';

const ContactsPage = () => {
  const { business } = useAuth();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (business?.id) {
      loadContacts();
    }
  }, [business]);

  const loadContacts = async () => {
    try {
      setLoading(true);
      const [customersRes, employeesRes] = await Promise.all([
        getUsers(business.id, 'customer'),
        getUsers(business.id, 'employee')
      ]);
      setCustomers(customersRes.data);
      setEmployees(employeesRes.data);
      
      // Also load from business key_contacts
      if (business.key_contacts) {
        const keyCustomers = business.key_contacts.filter(c => c.role === 'customer');
        const keyEmployees = business.key_contacts.filter(c => c.role === 'employee');
        
        // Merge without duplicates
        const mergedCustomers = [...customersRes.data];
        keyCustomers.forEach(kc => {
          if (!mergedCustomers.find(c => c.phone === kc.phone)) {
            mergedCustomers.push({ ...kc, id: kc.phone, from_contacts: true });
          }
        });
        
        const mergedEmployees = [...employeesRes.data];
        keyEmployees.forEach(ke => {
          if (!mergedEmployees.find(e => e.phone === ke.phone)) {
            mergedEmployees.push({ ...ke, id: ke.phone, from_contacts: true });
          }
        });
        
        setCustomers(mergedCustomers);
        setEmployees(mergedEmployees);
      }
    } catch (error) {
      console.error('Error loading contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?';
  };

  const ContactCard = ({ contact, type }) => (
    <div className="rounded-2xl" data-testid={`contact-${contact.id}`} style={{ background: '#1C1C1E', border: '1px solid #2C2C2E' }}>
      <div className="p-4">
        <div className="flex items-center gap-3">
          <Avatar className="h-12 w-12">
            <AvatarFallback className="text-sm font-medium" style={{ background: 'rgba(255,45,85,.15)', color: '#FF2D55' }}>
              {getInitials(contact.name)}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <p className="font-medium text-white">{contact.name || 'Unknown'}</p>
            <p className="text-sm mt-0.5" style={{ color: '#8E8E93' }}>{contact.phone}</p>
            {contact.type && (
              <span className="text-xs px-2 py-0.5 rounded-full mt-1 inline-block" style={{ background: '#2C2C2E', color: '#8E8E93' }}>
                {contact.type}
              </span>
            )}
          </div>
          <button className="p-2 rounded-full transition-opacity hover:opacity-70" style={{ color: '#8E8E93' }}>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen text-white" data-testid="contacts-page" style={{ background: '#000', fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', system-ui, sans-serif" }}>
      {/* Header */}
      <header className="p-4 sticky top-0 z-10" style={{ background: 'rgba(0,0,0,.85)', backdropFilter: 'saturate(180%) blur(20px)', borderBottom: '1px solid #1C1C1E' }}>
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </Button>
          <div className="flex-1">
            <h1 className="text-xl font-bold">Contacts</h1>
            <p className="text-sm" style={{ color: '#8E8E93' }}>Customers & Employees</p>
          </div>
          <button className="px-3 py-1.5 rounded-xl text-sm font-medium text-white flex items-center gap-1"
            style={{ background: 'linear-gradient(135deg, #FF2D55, #5856D6)' }}>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add
          </button>
        </div>
      </header>

      <Tabs defaultValue="customers" className="w-full">
        <TabsList className="w-full rounded-none h-12 border-b" style={{ background: '#000', borderColor: '#1C1C1E' }}>
          <TabsTrigger value="customers"
            className="flex-1 data-[state=active]:text-white data-[state=active]:shadow-none rounded-none"
            style={{ color: '#8E8E93' }}
            data-testid="customers-tab">
            Customers ({customers.length})
          </TabsTrigger>
          <TabsTrigger value="employees"
            className="flex-1 data-[state=active]:text-white data-[state=active]:shadow-none rounded-none"
            style={{ color: '#8E8E93' }}
            data-testid="employees-tab">
            Employees ({employees.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="customers" className="mt-0">
          <ScrollArea className="h-[calc(100vh-180px)]">
            <div className="p-4 space-y-3" data-testid="customers-list">
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: 'transparent transparent #FF2D55 transparent' }}></div>
                </div>
              ) : customers.length === 0 ? (
                <div className="rounded-2xl p-8 text-center" style={{ background: '#1C1C1E', border: '1px solid #2C2C2E' }}>
                  <p style={{ color: '#8E8E93' }}>No customers yet</p>
                </div>
              ) : (
                customers.map((customer) => (
                  <ContactCard key={customer.id} contact={customer} type="customer" />
                ))
              )}
            </div>
          </ScrollArea>
        </TabsContent>

        <TabsContent value="employees" className="mt-0">
          <ScrollArea className="h-[calc(100vh-180px)]">
            <div className="p-4 space-y-3" data-testid="employees-list">
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: 'transparent transparent #FF2D55 transparent' }}></div>
                </div>
              ) : employees.length === 0 ? (
                <div className="rounded-2xl p-8 text-center" style={{ background: '#1C1C1E', border: '1px solid #2C2C2E' }}>
                  <p style={{ color: '#8E8E93' }}>No employees yet</p>
                </div>
              ) : (
                employees.map((employee) => (
                  <ContactCard key={employee.id} contact={employee} type="employee" />
                ))
              )}
            </div>
          </ScrollArea>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ContactsPage;
